// ./src/components/features/VehicleFinancing/VehicleFinancingFilters/VehicleFinancingFilters.tsx
//
// Vehicle financing-specific filters wrapper around ExpenseFiltersToolbar.
// Adds financing type (Loan/Lease) and status (Active/Completed) filters
// as extraFilters (in drawer) and extraChips (in the active filters row).
//
// Draft state pattern:
// - Maintains draft values for financingType and status internally.
// - Exposes an ExtraFiltersHandle via ref so ExpenseFiltersDrawer can
//   call onOpen (sync applied → draft), onApply (commit draft → applied),
//   onClear (reset drafts), and hasDraftValues (for Clear button visibility).
// - The extraFilters rendered in the drawer bind to draft values, so
//   selecting a type or status does NOT trigger a data reload until
//   the user clicks Apply.
// - extraChips bind to the APPLIED values (from props), so chips only
//   appear after Apply is clicked.

import React, { useCallback, useMemo, useRef, useState } from "react";
import { Badge, CloseButton, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { ExpenseFiltersToolbar } from "@/components/features";
import { ExtraFiltersHandle } from "@/components/features/ExpenseFiltersToolbar/ExpenseFiltersDrawer";
import {
  FinancingTypeChipSelector,
} from "../FinancingTypeChipSelector";
import {
  FinancingStatusChipSelector,
  FinancingStatusValue,
} from "../FinancingStatusChipSelector";
import {
  FINANCING_TYPE,
  FinancingTypeValue,
  VEHICLE_FINANCING_STATUS,
} from "@/utils/api";

// ============================================
// Config Maps for Chip Labels/Colors
// ============================================

const FINANCING_TYPE_CONFIG: Record<string, { labelKey: string; color: string }> = {
  [FINANCING_TYPE.LOAN]: {
    labelKey: "vehicleFinancing:financingType.loan",
    color: "blue",
  },
  [FINANCING_TYPE.LEASE]: {
    labelKey: "vehicleFinancing:financingType.lease",
    color: "violet",
  },
};

const FINANCING_STATUS_CONFIG: Record<number, { labelKey: string; color: string }> = {
  [VEHICLE_FINANCING_STATUS.ACTIVE]: {
    labelKey: "vehicleFinancing:status.active",
    color: "green",
  },
  [VEHICLE_FINANCING_STATUS.COMPLETED]: {
    labelKey: "vehicleFinancing:status.completed",
    color: "gray",
  },
};

// ============================================
// Types
// ============================================

export interface VehicleFinancingFiltersProps {
  /** Handler for add button click */
  onAdd?: () => void;

  /** Handler for refresh button click */
  onRefresh?: () => void;

  /** Whether add button is disabled */
  addDisabled?: boolean;

  /** Whether refresh button is disabled */
  refreshDisabled?: boolean;

  /** Whether data is loading */
  loading?: boolean;

  /** Custom label for add button */
  addLabel?: string;

  /** Current applied financing type filter value */
  financingType?: FinancingTypeValue | null;

  /** Callback when financing type is applied */
  onFinancingTypeChange?: (value: FinancingTypeValue | null) => void;

  /** Current applied status filter value */
  status?: FinancingStatusValue | null;

  /** Callback when status is applied */
  onStatusChange?: (value: FinancingStatusValue | null) => void;

  /** Enable keyboard shortcuts (default: true) */
  enableShortcuts?: boolean;

  /**
   * Callback emitting a combined filter object built from all enabled sources.
   * Passed through to ExpenseFiltersToolbar.
   */
  onFiltersChange?: (filters: Record<string, unknown> | null) => void;
}

// ============================================
// Component
// ============================================

const VehicleFinancingFiltersComponent: React.FC<VehicleFinancingFiltersProps> = ({
  onAdd,
  onRefresh,
  addDisabled = false,
  refreshDisabled = false,
  loading = false,
  addLabel,
  financingType = null,
  onFinancingTypeChange,
  status = null,
  onStatusChange,
  enableShortcuts = true,
  onFiltersChange,
}) => {
  const { t } = useTranslation(["vehicleFinancing", "common"]);

  // ============================================
  // Draft State (used inside the drawer only)
  // ============================================

  const [draftFinancingType, setDraftFinancingType] = useState<FinancingTypeValue | null>(null);
  const [draftStatus, setDraftStatus] = useState<FinancingStatusValue | null>(null);

  // Refs to track latest applied values for getValues
  const financingTypeRef = useRef(financingType);
  financingTypeRef.current = financingType;
  const statusRef = useRef(status);
  statusRef.current = status;

  // ============================================
  // ExtraFiltersHandle — lifecycle contract with ExpenseFiltersDrawer
  // ============================================

  const extraFiltersRef = useRef<ExtraFiltersHandle>({
    onOpen: () => {
      setDraftFinancingType(financingType);
      setDraftStatus(status);
    },
    onApply: () => {
      onFinancingTypeChange?.(draftFinancingType);
      onStatusChange?.(draftStatus);
      const values: Record<string, unknown> = {};
      if (draftFinancingType !== null) {
        values.financingType = draftFinancingType;
      }
      if (draftStatus !== null) {
        values.status = draftStatus;
      }
      return values;
    },
    onClear: () => {
      setDraftFinancingType(null);
      setDraftStatus(null);
    },
    hasDraftValues: () => {
      return draftFinancingType !== null || draftStatus !== null;
    },
    getValues: () => {
      const values: Record<string, unknown> = {};
      if (financingTypeRef.current !== null) {
        values.financingType = financingTypeRef.current;
      }
      if (statusRef.current !== null) {
        values.status = statusRef.current;
      }
      return values;
    },
  });

  // Keep the ref callbacks up to date with latest state/props
  extraFiltersRef.current.onOpen = () => {
    setDraftFinancingType(financingType);
    setDraftStatus(status);
  };
  extraFiltersRef.current.onApply = () => {
    onFinancingTypeChange?.(draftFinancingType);
    onStatusChange?.(draftStatus);
    const values: Record<string, unknown> = {};
    if (draftFinancingType !== null) {
      values.financingType = draftFinancingType;
    }
    if (draftStatus !== null) {
      values.status = draftStatus;
    }
    return values;
  };
  extraFiltersRef.current.onClear = () => {
    setDraftFinancingType(null);
    setDraftStatus(null);
  };
  extraFiltersRef.current.hasDraftValues = () => {
    return draftFinancingType !== null || draftStatus !== null;
  };
  extraFiltersRef.current.getValues = () => {
    const values: Record<string, unknown> = {};
    if (financingTypeRef.current !== null) {
      values.financingType = financingTypeRef.current;
    }
    if (statusRef.current !== null) {
      values.status = statusRef.current;
    }
    return values;
  };

  // ============================================
  // Handlers — Chip removal (removes applied values directly)
  // ============================================

  const handleRemoveFinancingType = useCallback(() => {
    onFinancingTypeChange?.(null);
  }, [onFinancingTypeChange]);

  const handleRemoveStatus = useCallback(() => {
    onStatusChange?.(null);
  }, [onStatusChange]);

  // ============================================
  // Build Extra Chips (based on APPLIED values, not drafts)
  // ============================================

  const extraChips = useMemo(() => {
    const chips: React.ReactNode[] = [];

    // Financing type chip
    if (financingType) {
      const config = FINANCING_TYPE_CONFIG[financingType];
      if (config) {
        chips.push(
          <Badge
            key="financing-type"
            variant="light"
            color={config.color}
            size="lg"
            radius="sm"
            pr={3}
            style={{ flexShrink: 0 }}
            rightSection={
              <CloseButton
                size="xs"
                variant="transparent"
                onClick={handleRemoveFinancingType}
                aria-label={t("common:buttons.clear")}
              />
            }
          >
            {t(config.labelKey)}
          </Badge>
        );
      }
    }

    // Status chip
    if (status !== null) {
      const config = FINANCING_STATUS_CONFIG[status];
      if (config) {
        chips.push(
          <Badge
            key="financing-status"
            variant="light"
            color={config.color}
            size="lg"
            radius="sm"
            pr={3}
            style={{ flexShrink: 0 }}
            rightSection={
              <CloseButton
                size="xs"
                variant="transparent"
                onClick={handleRemoveStatus}
                aria-label={t("common:buttons.clear")}
              />
            }
          >
            {t(config.labelKey)}
          </Badge>
        );
      }
    }

    if (chips.length === 0) return undefined;
    return <>{chips}</>;
  }, [financingType, status, handleRemoveFinancingType, handleRemoveStatus, t]);

  // ============================================
  // Build Extra Filters Content (for drawer — uses DRAFT values)
  // ============================================

  const extraFilters = useMemo(() => {
    return (
      <Stack gap="md">
        <FinancingTypeChipSelector
          value={draftFinancingType}
          onChange={setDraftFinancingType}
          label={t("vehicleFinancing:filters.financingType", {
            defaultValue: "Type",
          })}
        />
        <FinancingStatusChipSelector
          value={draftStatus}
          onChange={setDraftStatus}
          label={t("vehicleFinancing:filters.status", {
            defaultValue: "Status",
          })}
        />
      </Stack>
    );
  }, [draftFinancingType, draftStatus, t]);

  // ============================================
  // Render
  // ============================================

  return (
    <ExpenseFiltersToolbar
      entityType="vehicle-financing"
      onAdd={onAdd}
      onRefresh={onRefresh}
      addDisabled={addDisabled}
      refreshDisabled={refreshDisabled}
      loading={loading}
      addLabel={addLabel}
      carFilterMode="single"
      showDateFilter={false}
      showTagFilter={false}
      enableShortcuts={enableShortcuts}
      extraFilters={extraFilters}
      extraChips={extraChips}
      extraFiltersRef={extraFiltersRef}
      onFiltersChange={onFiltersChange}
    />
  );
};

export const VehicleFinancingFilters = React.memo(VehicleFinancingFiltersComponent);